import { useEffect, useState } from "react";
import {
  Accessibility,
  X,
  Plus,
  Minus,
  Type,
  Contrast,
  Link as LinkIcon,
  RotateCcw,
  FileText,
  AlignJustify,
  MousePointer2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Switch } from "./ui/switch";

const STORAGE_KEY = "rc_a11y_settings_v1";

const defaults = {
  fontScale: 1, // 1, 1.1, 1.25, 1.5
  highContrast: false,
  highlightLinks: false,
  readableFont: false,
  largeCursor: false,
  letterSpacing: false,
};

const applySettings = (s) => {
  const root = document.documentElement;
  // Font scale via CSS variable on body
  document.body.style.fontSize = `${16 * s.fontScale}px`;

  // High contrast filter overlay
  if (s.highContrast) {
    root.setAttribute("data-a11y-contrast", "on");
  } else {
    root.removeAttribute("data-a11y-contrast");
  }
  // Highlight links
  if (s.highlightLinks) {
    root.setAttribute("data-a11y-links", "on");
  } else {
    root.removeAttribute("data-a11y-links");
  }
  // Readable font
  if (s.readableFont) {
    root.setAttribute("data-a11y-readable", "on");
  } else {
    root.removeAttribute("data-a11y-readable");
  }
  // Large cursor
  if (s.largeCursor) {
    root.setAttribute("data-a11y-cursor", "on");
  } else {
    root.removeAttribute("data-a11y-cursor");
  }
  // Letter spacing
  if (s.letterSpacing) {
    root.setAttribute("data-a11y-spacing", "on");
  } else {
    root.removeAttribute("data-a11y-spacing");
  }
};

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [statementOpen, setStatementOpen] = useState(false);
  const [settings, setSettings] = useState(defaults);

  // Load persisted settings
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = { ...defaults, ...JSON.parse(raw) };
        setSettings(parsed);
        applySettings(parsed);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const update = (next) => {
    setSettings(next);
    applySettings(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      // ignore
    }
  };

  const reset = () => update(defaults);

  const adjustFont = (delta) => {
    const steps = [0.9, 1, 1.1, 1.25, 1.5];
    const idx = steps.indexOf(settings.fontScale);
    const safeIdx = idx === -1 ? 1 : idx;
    const nextIdx = Math.min(steps.length - 1, Math.max(0, safeIdx + delta));
    update({ ...settings, fontScale: steps[nextIdx] });
  };

  return (
    <>
      {/* Inline CSS rules for accessibility data-attributes */}
      <style>{`
        html[data-a11y-contrast="on"] body {
          filter: contrast(1.25) saturate(1.1);
        }
        html[data-a11y-contrast="on"] body * {
          text-shadow: none !important;
        }
        html[data-a11y-links="on"] a:not([data-a11y-trigger]) {
          text-decoration: underline !important;
          text-decoration-thickness: 2px !important;
          text-underline-offset: 3px !important;
          background-color: rgba(255, 235, 130, 0.25) !important;
          padding: 0 2px;
          border-radius: 2px;
        }
        html[data-a11y-readable="on"] body,
        html[data-a11y-readable="on"] body * {
          font-family: 'Inter', Arial, sans-serif !important;
          letter-spacing: 0.01em;
        }
        html[data-a11y-cursor="on"] body,
        html[data-a11y-cursor="on"] body * {
          cursor: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='44' height='44' viewBox='0 0 24 24' fill='black' stroke='white' stroke-width='1.5'><path d='M3 2 L21 12 L13 14 L11 22 Z'/></svg>") 0 0, auto !important;
        }
        html[data-a11y-spacing="on"] body,
        html[data-a11y-spacing="on"] body * {
          letter-spacing: 0.06em !important;
          word-spacing: 0.1em !important;
          line-height: 1.8 !important;
        }
      `}</style>

      {/* Floating button (bottom-left) */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open accessibility tools"
        className="fixed bottom-5 left-5 z-50 group flex items-center gap-2 h-14 w-14 hover:w-[170px] overflow-hidden rounded-full bg-[hsl(var(--rc-deep))] text-white shadow-[0_14px_36px_-10px_rgba(60,40,90,0.55)] hover:shadow-[0_20px_44px_-12px_rgba(60,40,90,0.7)] transition-all duration-300 pl-[14px]"
      >
        <Accessibility className="w-7 h-7 shrink-0" />
        <span className="text-[13px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          Accessibility
        </span>
      </button>

      {/* Settings dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md p-0 gap-0 overflow-hidden !left-5 !top-auto !bottom-24 !translate-x-0 !translate-y-0 data-[state=open]:slide-in-from-left-8 data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-left-8 data-[state=closed]:slide-out-to-top-0 sm:rounded-2xl">
          <DialogHeader className="px-6 pt-6 pb-4 bg-[hsl(var(--rc-deep))] text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <Accessibility className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-white text-[18px] font-semibold">
                  Accessibility Tools
                </DialogTitle>
                <DialogDescription className="text-white/70 text-[12.5px]">
                  Adjust the site to your needs. Settings are saved on this
                  device.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh]">
            <div className="px-6 py-5 space-y-6">
              {/* Font size */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Type className="w-4 h-4 text-[hsl(var(--rc-purple))]" />
                    <span className="text-[14px] font-medium text-[hsl(var(--rc-charcoal))]">
                      Text Size
                    </span>
                  </div>
                  <span className="text-[12px] text-[hsl(var(--muted-foreground))]">
                    {Math.round(settings.fontScale * 100)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => adjustFont(-1)}
                    aria-label="Decrease text size"
                    className="h-10 w-10"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <div className="flex-1 h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                    <div
                      className="h-full bg-[hsl(var(--rc-purple))] transition-all"
                      style={{
                        width: `${((settings.fontScale - 0.9) / (1.5 - 0.9)) * 100}%`,
                      }}
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => adjustFont(1)}
                    aria-label="Increase text size"
                    className="h-10 w-10"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="h-px bg-[hsl(var(--border))]" />

              {[
                {
                  key: "highContrast",
                  icon: Contrast,
                  label: "High Contrast",
                  desc: "Boost contrast across the page.",
                },
                {
                  key: "highlightLinks",
                  icon: LinkIcon,
                  label: "Highlight Links",
                  desc: "Make links stand out clearly.",
                },
                {
                  key: "readableFont",
                  icon: FileText,
                  label: "Readable Font",
                  desc: "Switch headings to a sans-serif typeface.",
                },
                {
                  key: "letterSpacing",
                  icon: AlignJustify,
                  label: "Letter & Line Spacing",
                  desc: "Increase spacing for easier reading.",
                },
                {
                  key: "largeCursor",
                  icon: MousePointer2,
                  label: "Large Cursor",
                  desc: "Use an enlarged cursor.",
                },
              ].map(({ key, icon: Icon, label, desc }) => (
                <div
                  key={key}
                  className="flex items-start justify-between gap-4"
                >
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-9 h-9 rounded-lg bg-[hsl(var(--rc-purple-soft))] flex items-center justify-center text-[hsl(var(--rc-purple))] shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-[14px] font-medium text-[hsl(var(--rc-charcoal))]">
                        {label}
                      </div>
                      <div className="text-[12.5px] text-[hsl(var(--muted-foreground))] mt-0.5">
                        {desc}
                      </div>
                    </div>
                  </div>
                  <Switch
                    checked={settings[key]}
                    onCheckedChange={(v) =>
                      update({ ...settings, [key]: v })
                    }
                    aria-label={label}
                  />
                </div>
              ))}

              <div className="h-px bg-[hsl(var(--border))]" />

              <div className="flex flex-col sm:flex-row gap-2.5">
                <Button
                  variant="outline"
                  onClick={reset}
                  className="flex-1 h-11"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset all
                </Button>
                <Button
                  onClick={() => setStatementOpen(true)}
                  className="flex-1 h-11 bg-[hsl(var(--rc-deep))] hover:bg-[hsl(var(--rc-purple))]"
                >
                  Accessibility statement
                </Button>
              </div>
            </div>
          </ScrollArea>

          <button
            aria-label="Close"
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogContent>
      </Dialog>

      {/* Statement dialog */}
      <Dialog open={statementOpen} onOpenChange={setStatementOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-[24px] text-[hsl(var(--rc-deep))]">
              Website Accessibility Statement
            </DialogTitle>
            <DialogDescription className="text-[12px] tracking-[0.16em] uppercase">
              Last Updated: February 27, 2026
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-4">
            <div className="space-y-5 text-[14.5px] leading-[1.75] text-[hsl(var(--rc-slate))]">
              <section>
                <h4 className="font-serif-display text-[18px] font-semibold text-[hsl(var(--rc-deep))] mb-1.5">
                  Our Commitment to Accessibility
                </h4>
                <p>
                  We are committed to ensuring digital accessibility for people
                  with disabilities. We are continually improving the user
                  experience for everyone and applying relevant accessibility
                  standards to enhance usability and inclusivity.
                </p>
                <p className="mt-2">
                  Our goal is to provide a website that is accessible to the
                  widest possible audience, regardless of technology or
                  ability.
                </p>
              </section>

              <section>
                <h4 className="font-serif-display text-[18px] font-semibold text-[hsl(var(--rc-deep))] mb-1.5">
                  Accessibility Standards
                </h4>
                <p>
                  We strive to conform to the Web Content Accessibility
                  Guidelines (WCAG) 2.1 Level AA, which are widely recognized as
                  the industry standard for website accessibility.
                  Accessibility is an ongoing process, and we are actively
                  working toward improving the accessibility and usability of
                  our website.
                </p>
              </section>

              <section>
                <h4 className="font-serif-display text-[18px] font-semibold text-[hsl(var(--rc-deep))] mb-1.5">
                  Accessibility Technology & Tools
                </h4>
                <p>
                  To support our accessibility efforts, we have deployed
                  industry-standard accessibility technology designed to
                  enhance usability and assist visitors with disabilities.
                  These tools may provide features such as:
                </p>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  <li>Screen reader enhancements</li>
                  <li>Keyboard navigation improvements</li>
                  <li>Color contrast adjustments</li>
                  <li>Text size and spacing controls</li>
                  <li>Focus indicators</li>
                  <li>Additional accessibility interface options</li>
                </ul>
                <p className="mt-2">
                  This technology helps us improve accessibility while we
                  continue broader auditing, remediation, and development
                  efforts.
                </p>
              </section>

              <section>
                <h4 className="font-serif-display text-[18px] font-semibold text-[hsl(var(--rc-deep))] mb-1.5">
                  Ongoing Improvement
                </h4>
                <p>Accessibility is an ongoing initiative. We are:</p>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  <li>Reviewing site content and functionality regularly</li>
                  <li>Working with developers and accessibility tools</li>
                  <li>Identifying and remediating barriers</li>
                  <li>
                    Incorporating accessibility into future design and
                    development
                  </li>
                </ul>
                <p className="mt-2">
                  We understand accessibility is a continuous journey and are
                  committed to making improvements over time.
                </p>
              </section>

              <section>
                <h4 className="font-serif-display text-[18px] font-semibold text-[hsl(var(--rc-deep))] mb-1.5">
                  Third-Party Content
                </h4>
                <p>
                  Some areas of the website may include third-party content,
                  plugins, or integrations that are not fully controlled by us.
                  While we encourage vendors to provide accessible content, we
                  cannot guarantee accessibility of third-party features.
                  However, we welcome feedback regarding any issues
                  encountered.
                </p>
              </section>

              <section>
                <h4 className="font-serif-display text-[18px] font-semibold text-[hsl(var(--rc-deep))] mb-1.5">
                  Feedback & Assistance
                </h4>
                <p>
                  If you experience difficulty accessing any part of this
                  website, need assistance, or would like to request content in
                  an alternative format, please contact us. Please include:
                </p>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  <li>The page URL</li>
                  <li>Description of the issue</li>
                  <li>Your preferred contact method</li>
                </ul>
                <p className="mt-2">
                  Contact: information on our Contact Us page. We aim to
                  respond within a reasonable timeframe and will make
                  reasonable efforts to provide accessible alternatives.
                </p>
              </section>

              <section>
                <h4 className="font-serif-display text-[18px] font-semibold text-[hsl(var(--rc-deep))] mb-1.5">
                  Good Faith Efforts
                </h4>
                <p>
                  We are making good-faith efforts to comply with accessibility
                  standards and improve usability for individuals with
                  disabilities. Despite our efforts, some content may not yet
                  fully meet the strictest accessibility standards due to the
                  evolving nature of technology and ongoing updates. We
                  appreciate your understanding as we continue improving
                  accessibility.
                </p>
              </section>

              <section>
                <h4 className="font-serif-display text-[18px] font-semibold text-[hsl(var(--rc-deep))] mb-1.5">
                  Terms & Conditions — Accessibility Clause
                </h4>
                <p>
                  We are committed to providing an accessible website
                  experience for all users and strive to follow recognized
                  accessibility standards, including WCAG guidelines. We have
                  implemented industry-standard accessibility tools and
                  continue to review, update, and improve our website to
                  enhance accessibility.
                </p>
                <p className="mt-2">
                  Because accessibility is an ongoing process, some areas of
                  the website may not yet fully conform to all accessibility
                  standards. If you encounter accessibility barriers, please
                  contact us so we can provide assistance, offer alternative
                  access, and address the issue where feasible.
                </p>
                <p className="mt-2">
                  Nothing in these Terms is intended to limit any rights
                  available under applicable accessibility laws.
                </p>
              </section>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AccessibilityWidget;
